import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import type { RowCardState } from '@/components/ui/row-card'
import { TooltipPanel } from '@/components/ui/Tooltip'
import { cn } from '@/lib/utils'

/** Air between the anchor and the card. */
const GAP = 8
/** The card keeps this far from the viewport's left and right edges. */
const EDGE = 12
/** Under this much room above the anchor the card opens below it instead. */
const FLIP_ABOVE = 72

interface Placement {
  left: number
  top: number
  below: boolean
}

/**
 * Draws a `useRowCard` card: centred over the anchor, 8 above it, below it when less than 72px of
 * room is left above, and held 12 off the viewport's sides — the mock's `brb-tip` placement.
 *
 * The tooltip skin (`TooltipPanel variant="card"`): the toast fill under a default hairline and the
 * tooltip's drop, a 12/600 title, label/value rows, the tertiary note. It rises 4px into place and
 * fades in over the med step (the mock's `brb-tip-in`, 220 ease-out), from below when it sits under
 * the anchor; reduced motion drops the travel with every other transition.
 *
 * Pinned, it takes the pointer, so an `action` link in it can be clicked; unpinned it is
 * see-through to the pointer, so it never sits between the mouse and the next row.
 */
export default function RowCard<K extends string>({ card }: { card: RowCardState<K> }) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [placement, setPlacement] = useState<Placement | null>(null)
  const { open } = card

  // Unplaced on every new anchor or content, so the card is measured before it animates.
  const [placedFor, setPlacedFor] = useState(open)
  if (placedFor !== open) {
    setPlacedFor(open)
    setPlacement(null)
  }

  useLayoutEffect(() => {
    const panel = panelRef.current
    if (open === null || panel === null || placement !== null) return
    const width = panel.offsetWidth
    const height = panel.offsetHeight
    const viewport = document.documentElement.clientWidth
    const centre = open.rect.left + open.rect.width / 2
    const left = Math.max(EDGE, Math.min(viewport - width - EDGE, centre - width / 2))
    const below = open.rect.top - height - GAP < FLIP_ABOVE
    setPlacement({
      left,
      top: below ? open.rect.bottom + GAP : open.rect.top - height - GAP,
      below,
    })
  }, [open, placement])

  if (open === null) return null

  return createPortal(
    <div
      // A new element once placed, so its entrance starts from the side it landed on.
      key={placement === null ? 'measure' : 'placed'}
      ref={panelRef}
      id={card.id}
      style={{
        left: placement?.left ?? 0,
        top: placement?.top ?? 0,
        visibility: placement === null ? 'hidden' : undefined,
      }}
      className={cn(
        'fixed z-50',
        open.pinned ? 'pointer-events-auto' : 'pointer-events-none',
        placement !== null &&
          'transition-[opacity,translate] duration-med ease-out starting:opacity-0',
        placement !== null &&
          (placement.below ? 'starting:-translate-y-1' : 'starting:translate-y-1'),
      )}
    >
      <TooltipPanel
        variant="card"
        fade={false}
        title={open.content.title}
        rows={open.content.rows}
        extra={open.content.extra}
        note={open.content.note}
        {...(open.pinned && open.content.action !== undefined ? { hint: open.content.action } : {})}
      />
    </div>,
    document.body,
  )
}
